import { SOURCE_TAB } from '@va/constants';
import { VisitorType } from '@va/dashboard/api-client/sessions.api';
import {
  CountryLocationIcon,
  DetailsCell,
  PageVisitsInSessionCell,
  VideoRecordingButton,
} from '@va/dashboard/components';
import { useLocale, useTranslate } from '@va/localization';
import { Paragraph, fontWeights, paragraphSizes } from '@va/ui/design-system';
import { getFormattedDateAndTime } from '@va/util/helpers';
import { Ip2CompanyInfo, IpValueNode } from '../cells';
import { LatestVisitorsItemV2 } from '../latest-visitors-card/useLatestVisitors';

type VisitorsListItemProps = {
  visitSession: LatestVisitorsItemV2;
};

export const VisitorsListItem = ({ visitSession }: VisitorsListItemProps) => {
  const {
    countryCode,
    latitude,
    longitude,
    visitorType,
    sessionStartUnix,
    companyOrgName,
    companyOrgType,
    browserName,
    deviceType,
    platform,
    sessionKey,
    unfilteredVisitCount,
    hasRecording,
    ip,
  } = visitSession;
  const translate = useTranslate();
  const { locale } = useLocale();

  return (
    <div className='w-full'>
      <div className='flex gap-3'>
        <div className='shrink-0'>
          <CountryLocationIcon
            countryCode={countryCode}
            location={{ lat: latitude, lng: longitude }}
            sessionId={sessionKey}
          />
        </div>
        <div className='w-full overflow-hidden'>
          <div>
            <Paragraph className='inline mr-1' weight={fontWeights.medium}>
              {translate(`card.latestVisits.${VISITOR_TYPE_TRANSLATION_KEY_MAP[visitorType]}`)}
            </Paragraph>
            <Paragraph colorClassName='text-gray-devil' size={paragraphSizes.tiny} className='inline'>
              {getFormattedDateAndTime(sessionStartUnix, locale)}
            </Paragraph>
          </div>
          <div className=''>
            <IpValueNode className='text-12 text-gray-charcoal' ip={ip} countryCode={countryCode} />
            <Ip2CompanyInfo companyName={companyOrgName} companyType={companyOrgType} />
          </div>
        </div>
      </div>
      <div className='pl-12 flex gap-3 justify-between w-full'>
        <DetailsCell
          className='gap-0!'
          browser={browserName}
          device={deviceType}
          platform={platform}
          smallVersion={true}
        />
        <div className='flex items-center gap-1 sm-initial:gap-2'>
          <PageVisitsInSessionCell
            sessionId={sessionKey}
            pageVisitsCount={unfilteredVisitCount}
            countryCode={countryCode}
          />
          <VideoRecordingButton
            sourceTab={SOURCE_TAB.LATEST_VISITORS}
            sessionInfo={{ unixTs: sessionStartUnix, id: sessionKey, hasRecording: hasRecording }}
          />
        </div>
      </div>
    </div>
  );
};

export const VISITOR_TYPE_TRANSLATION_KEY_MAP = {
  [VisitorType.NewVisitor]: 'newVisitor',
  [VisitorType.Returning]: 'returningVisitor',
};
